import React, { useEffect, useState, useMemo } from "react";
import {
  AudioPlayer,
  Backdrop,
  Button,
  ChatHistory,
  LogoLoader,
  Modal,
  StatusTags,
  TableAdv,
} from "../../../../../../components";
import { mapTableRows } from "../../../../../../utils";
import { useDispatch, useSelector } from "react-redux";
import { usePagination } from "../../../../../../hooks";
import { fetchAllAiCallsApi } from "../../../../../../redux/slices/aicalls/aicallsApi";
import { totalCountStart } from "../../../../../../redux/slices/totalCount/totalCountSlice";
import {
  convertFromUTC,
  extractDateAndTimeFromFormatted,
} from "../../../../../../utils/dateUtils";
import Chat from "../../../../../../components/Chat/Chat";

const formatDateTime = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);

  const optionsDate = { year: "numeric", month: "long", day: "2-digit" };
  const formattedDate = date.toLocaleDateString("en-US", optionsDate);

  const optionsTime = {
    hour: "2-digit",
    minute: "2-digit",
    // second: "2-digit",
    hour12: true,
  };
  const formattedTime = date.toLocaleTimeString("en-US", optionsTime);

  return `${formattedDate} ${formattedTime}`;
};

const AiCallHistory = ({ jobPostId }) => {
  const [viewCallModal, setViewCallModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [aiCalls, setAiCalls] = useState([]);
  const [audioFile, setAudioFile] = useState(null);
  const [selectedCallContext, setSelectedCallContext] = useState(null);
  const dispatch = useDispatch();
  const { page, limit, setPage, handleLimitChange } = usePagination(10, (newPage, newLimit) => { });
  const { value } = useSelector((state) => state.totalCount);
  const totalCount = value?.aicallhistory?.data || 0

  const aiCallStatusMap = {
    2: "Success",
    3: "Failed",
    4: "Skipped",
    6: "Not Answered",
    7: "Interested",
    8: "Not Interested",
    9: "Cancel"
  }

  const columns = [
    {
      key: "candidateName",
      label: "Candidate Name",
      columnMenu: false,
    },
    {
      key: "role",
      label: "Role",
      columnMenu: false,
    },
    {
      key: "startedAt",
      label: "Started Time",
      columnMenu: false,
    },
    {
      key: "status",
      label: "Status",
      columnMenu: false,
      render: (row) => (
        <div className="flex flex-column flex-wrap">
          <StatusTags tag={row?.status} />
          {row?.status === "Skipped" && (
            <p className="italic">{row?.skipReason}</p>
          )}
        </div>
      ),
    },
    {
      key: "callType",
      label: "Call Type"
    },
    {
      key: "duration",
      label: "Call Duration",
      columnMenu: false,
      render: (row) => (
        <div>{`${Math.floor(row.durationSeconds / 60)} min ${Math.round(row.durationSeconds % 60)} sec`}</div>
      )
    },
    {
      key: "endedReason",
      label: "Ended Reason",
      columnMenu: false,
    },    
    {
      key: "audio",
      label: "Audio",
      columnMenu: false,
      render: (row) => (
        <AudioPlayer file={row?.audio} download={{ isDownloadable: true }} onDownload={handleDownload} />
      ),
    },
    {
      key: "success_score",
      label: "Success Score",
      columnMenu: false,
    },
    {
      key: "action",
      label: "Action",
      columnMenu: false,
      render: (row) => (
        <Button
          type="icon"
          icon={"visibility"}
          variant="text"
          color="black"
          onClick={() => {
            setViewCallModal(true);
            setAudioFile(row?.audio)
            setSelectedCallContext(row)
          }}
        />
      ),
    },
  ];

  const rows = aiCalls.map((call) => {
    const localDateTime = convertFromUTC(call?.startedAt);
    const { date, time } = extractDateAndTimeFromFormatted(localDateTime);

    return {
      candidateName: `${call?.candidateId?.firstName || ""} ${call?.candidateId?.lastName || ""}`,
      role: call?.jobPostId?.jobId?.title,
      date: date,
      time: time,
      status: aiCallStatusMap[call?.status],
      skipReason: call?.skipReason || "",
      durationSeconds: call?.durationSeconds || 0,
      audio: call?.stereoRecordingUrl,
      transcript: call?.transcript || "",
      summary: call?.structuredOutput?.candidate_response_summary,
      endedReason: call?.endedReason,
      startedAt: formatDateTime(convertFromUTC(call?.startedAt)),
      success_score: call?.structuredOutput?.success_score || "-",
      // callType: call?.callType === 1 ? "Incoming" : call?.callType === 2 ? "Incoming" : "Outgoing",
      callType:  call?.callType === 2 ? "Incoming" : "Outgoing",
    }
  });

  const fetchAiCalls = async () => {
    try {
      setLoading(true);
      const payload = {
        coll: "AiCall",
        fil: {
          jobPostId: { type: "NONE", value: jobPostId },
          status: { type: "IN", value: [2,3,4,6,7,8,9] }
        },
        ref: "Candidate JobPost",
        incl: ["uiid firstName lastName", "uiid jobId"],
        extret: "uiid firstName lastName"
      }
      const response = await fetchAllAiCallsApi(payload);
      if (response?.success) {
        setAiCalls(response?.data?.data);
      }
    } catch (error) {
      console.error("Error fetching AI Calls:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (file) => {
    const url = file;
    try {
      setLoading(true);
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `audio-${Date.now()}.wav`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(
      totalCountStart({
        collection: "aicalls",
        coll: "AiCall",
        filters: { 
          jobPostId: { type: "NONE", value: jobPostId },
          status: { type: "IN", value: [2,3,4,6,7,8] }
        },
        tname: "aicallhistory",
        app: "ats",
      })
    );
  }, []);

  useEffect(() => {
    fetchAiCalls();
  }, [jobPostId, dispatch, page, limit]);

  // const { parsedChats, dynamicUsers } = useMemo(() => {
  //   if (!selectedCallContext) return { parsedChats: [], dynamicUsers: [] };

  //   const dynamicUsers = [
  //     { id: 1, name: "AI Recruiter", profilePhotoUrl: null },
  //     {
  //       id: 2,
  //       name: selectedCallContext.candidateName || "Candidate",
  //       profilePhotoUrl: null,
  //     },
  //   ];

  //   const rawTranscript = selectedCallContext.transcript || "";
  //   const lines = rawTranscript
  //     .split("\n")
  //     .filter((line) => line.trim() !== "");

  //   let messages = [];
  //   const baseTime = new Date().getTime();

  //   lines.forEach((line, index) => {
  //     const timestamp =
  //       selectedCallContext?.date + " " + selectedCallContext?.time;

  //     if (line.startsWith("AI: ")) {
  //       messages.push({
  //         id: index,
  //         senderId: 1,
  //         message: line.substring(4),
  //         timestamp,
  //       });
  //     } else if (line.startsWith("User: ")) {
  //       messages.push({
  //         id: index,
  //         senderId: 2,
  //         message: line.substring(6),
  //         timestamp,
  //       });
  //     } else {
  //       if (messages.length > 0) {
  //         messages[messages.length - 1].message += "\n" + line;
  //       } else {
  //         messages.push({
  //           id: index,
  //           senderId: 1,
  //           message: line,
  //           timestamp,
  //         });
  //       }
  //     }
  //   });

  //   return { parsedChats: messages, dynamicUsers };
  // }, [selectedCallContext]);

  const mappedRows = mapTableRows(rows, columns);

  const { chatsByDate, dynamicUsers } = useMemo(() => {
    if (!selectedCallContext) return { chatsByDate: [], dynamicUsers: [] };

    const dynamicUsers = [
      { id: 1, name: "AI Recruiter", profilePhotoUrl: null },
      {
        id: 2,
        name: selectedCallContext.candidateName || "Candidate",
        profilePhotoUrl: null,
      },
    ];

    const rawTranscript = selectedCallContext.transcript || "";
    const lines = rawTranscript
      .split("\n")
      .filter((line) => line.trim() !== "");

    let messages = [];

    lines.forEach((line, index) => {
      const timestamp =
        selectedCallContext?.date + " " + selectedCallContext?.time;

      if (line.startsWith("AI: ")) {
        messages.push({
          id: index,
          senderId: 1,
          message: line.substring(4),
          timestamp,
        });
      } else if (line.startsWith("User: ")) {
        messages.push({
          id: index,
          senderId: 2,
          message: line.substring(6),
          timestamp,
        });
      } else {
        if (messages.length > 0) {
          messages[messages.length - 1].message += "\n" + line;
        } else {
          messages.push({ id: index, senderId: 1, message: line, timestamp });
        }
      }
    });

    // Group messages by date for Chat component
    const grouped = messages.reduce((acc, msg) => {
      const date = selectedCallContext?.date || "Unknown Date";
      const existing = acc.find((g) => g.date === date);
      if (existing) {
        existing.messages.push(msg);
      } else {
        acc.push({ date, messages: [msg] });
      }
      return acc;
    }, []);

    return { chatsByDate: grouped, dynamicUsers };
  }, [selectedCallContext]);

  return (
    <div className="flex flex-col mt-5 overflow-auto">
      <div className="flex flex-grow overflow-auto gap-4 mx-0.5">
        <TableAdv
          showSlNo
          columns={columns}
          rows={mappedRows}
          totalCount={totalCount}
          serverPagination={true}
          page={page}
          limit={limit}
          toolbarRight={{
            fullScreen: true
          }}
        />
      </div>
      <Modal
        title={"Candidate Information"}
        isOpen={viewCallModal}
        onClose={() => setViewCallModal(false)}
        width="max-w-5xl"
      >
        <div className="flex flex-col gap-3 ">
          <div className="flex justify-between shadow-lg p-4 rounded">
            <div className="flex flex-col gap-1">
              <h6>Duration of call</h6>
              <p className="text-[12px]">
                {selectedCallContext?.durationSeconds
                  ? `${Math.floor(selectedCallContext.durationSeconds / 60)} min ${Math.round(selectedCallContext.durationSeconds % 60)} sec`
                  : selectedCallContext?.duration || "N/A"
                }
              </p>
              <p className="text-[12px]">
                {selectedCallContext?.date} {selectedCallContext?.time ? `• ${selectedCallContext.time}` : ""}
              </p>
            </div>
            <AudioPlayer file={audioFile} download={{ isDownloadable: true }} onDownload={handleDownload} />
          </div>
          {selectedCallContext?.summary && (
            <div className="shadow-lg p-4 rounded">
              <h6 className="text-[12px]">Summary</h6>
              <p className="text-[12px]">{selectedCallContext?.summary}</p>
            </div>
          )}
          {/* <ChatHistory chats={parsedChats} users={dynamicUsers} /> */}

          {chatsByDate.length > 0 && (
            <div className="h-[400px] overflow-hidden border border-gray-200 rounded-md">
              <Chat
                chatsByDate={chatsByDate}
                users={dynamicUsers}
                showInput={false}
                showActions={false}
                loginUserId={1}
              />
            </div>
          )}
        </div>
      </Modal>
      <Backdrop isOpen={loading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
}

export default AiCallHistory